import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon } from '@persona-id/design-tokens/icons';
import { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import HelpIcon from 'dashboard/components-lib/HelpIcon';
import SearchInput from 'dashboard/components-lib/SearchInput';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { formatNumber } from 'dashboard/lib/utils/formatters';
import { VerificationCheckV2Metrics } from 'dashboard/models/metrics';
import ExpandedHeader from 'dashboard/views/DashboardVerificationAnalyticsChecksV2View/ExpandedHeader';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import useToggle from 'lib/hooks/useToggle';
import { CheckName } from 'lib/verificationCheck';

const MAX_LEGEND_HEIGHT = 376;

const Container = styled.div`
  padding-top: ${Spacing[2]};
  margin-bottom: ${Spacing[2]};

  max-height: ${MAX_LEGEND_HEIGHT}px;
  overflow-x: hidden;
  overflow-y: auto;
`;

const StyledButton = styled.div`
  margin-top: ${Spacing[8]};
  float: left;
  width: 200px;
  font-weight: ${FontWeight.Regular};
  font-size: 18px;
  line-height: 10px;
`;

const FullLegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${Spacing[4]};
  font-family: ${({ theme }) => theme.text.fontFamily.base};
  width: 100%
  align-items: center;
`;

interface ColoredDotProps {
  color?: string;
  active: boolean;
}

const ColoredCircle = styled.div<ColoredDotProps>`
  display: inline-block;
  margin-left: 5px;
  margin-right: 5px;
  margin-bottom: 0px;
  border-radius: 50%;
  border-style: none;
  border-width: 0.5px;
  background: ${({ active, color }) => (active ? color : Color.CoreSlate300)};
  opacity: ${({ active }) => (active ? 1 : 0.3)};
  height: 10px;
  width: 10px;
`;

const LegendText = styled.span`
  font-style: normal;
  font-weight: ${FontWeight.Light};
  font-size: 14px;
  line-height: 10px;
`;

const StyledRow = styled(TableTR)`
  line-height: 10px;
`;

const StyledTD = styled(TableTD)`
  padding: ${Spacing[2]};
  padding-left: ${Spacing[4]};
`;

interface ChecksExpandedLegendProps {
  dataTest?: string;
  canSort: boolean;
  summaryData: Record<string, VerificationCheckV2Metrics>;
  colorsMap: Partial<Record<CheckName, string>>;
  activeKeys: string[];
  handleLegendClick: (dataKey: string) => void;
  handleFillLegend: (dataKeys: string[]) => void;
  unrequiredChecks: Record<string, string>;
  requiredChecks: Record<string, string>;
  hoveredKey?: string;
  setHoveredKey: (dataKey: string | undefined) => void;
}

interface CheckRow {
  id: string;
  name: CheckName;
  description: string;
  requirement: 'Required' | 'Non required';
  pass_rate: number;
}

const SORTABLE_ELEMENTS = ['name', 'requirement', 'pass_rate'];

const CheckHeader = styled(TableTH)`
  width: 50px;
  margin-right: 0px;
`;

const CheckIcon = styled(HelpIcon)`
  margin-bottom: -5px;
`;

const StyledHeader = styled(ExpandedHeader)`
  font-weight: ${FontWeight.Regular};
  font-size: 14px;
  line-height: 20px;
`;

const LegendSearch = styled(SearchInput)`
  float: right;
  width: 300px;
  padding-bottom: -5px;
`;

const StyledIconCaret = styled(CaretDownIcon)<{ expanded: boolean }>`
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(${({ expanded }) => (expanded ? '0' : '-90deg')});
  justify-self: center;
  color: ${({ theme }) => theme.color.text.primary};
  align-items: center;
`;

export const MetricsDivider = styled.div`
  margin-top: ${Spacing[2]};
  padding-top: ${BASE / 4}px;
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  width: 100%;
`;

const ChecksExpandedLegend: FC<ChecksExpandedLegendProps> = ({
  dataTest,
  canSort,
  summaryData,
  colorsMap,
  activeKeys,
  hoveredKey,
  setHoveredKey,
  handleLegendClick,
  handleFillLegend,
  requiredChecks,
  unrequiredChecks,
}) => {
  // Sort Params will be the state, we'll just update them as i
  // Remove initial sorting
  const [sortParams, setSortParams] = useState<{ sort?: string; shouldSortDescending: boolean }>({
    sort: undefined,
    shouldSortDescending: true,
  });

  // Heres our search Term, will be updated on input
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Handling the sorting
  const handleSortUpdate = useCallback(
    (sortParams: SortParams) => {
      setSortParams(sortParams);
    },
    [setSortParams],
  );

  const [isCollapsed, { toggle: flipCollapse }] = useToggle(false);

  // Clicking the column header
  const handleColumnHeaderClick = (columnName: string) => {
    if (!canSort) {
      return;
    }

    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  // Here, we'll actually sort the data that goes into the funnel. First, we need to calculate all
  // of the attributes and then we can sort them
  const checks: CheckRow[] = [];
  Object.keys(summaryData).forEach((checkName) => {
    const checkData = summaryData[checkName];
    // TODO: [jonny] CONSTANTIZE
    const required = requiredChecks[checkName] ? 'Required' : 'Non required';
    const parsedName = requiredChecks[checkName]
      ? requiredChecks[checkName]
      : unrequiredChecks[checkName];
    const check = CHECK_DISPLAY_CONFIG[parsedName as CheckName];

    if (check != null) {
      const passRate =
        (checkData['pass_count'] / (checkData['pass_count'] + checkData['failure_count'])) * 100;

      checks.push({
        id: checkName,
        name: check.name as CheckName,
        description: check.description,
        requirement: required,
        pass_rate: passRate,
      });
    }
  });

  const isEmpty = checks.length === 0;

  // handle a search
  const filteredChecks: CheckRow[] =
    searchTerm.trim() !== ''
      ? checks.filter(
          (check) =>
            // Only can search on name, type and requirement
            // Lowercase all of the terms to ensure were not doing something dumb
            check.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            check.requirement.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : checks;

  // Conditional Sort function
  function compare(a: CheckRow, b: CheckRow) {
    const firstElement = a[sortParams.sort as keyof CheckRow];
    const secondElement = b[sortParams.sort as keyof CheckRow];
    const remainingSortElements: string[] = [...SORTABLE_ELEMENTS].filter(
      (element) => element !== sortParams.sort,
    );

    if (firstElement < secondElement) {
      return sortParams.shouldSortDescending ? -1 : 1;
    }
    if (firstElement > secondElement) {
      return sortParams.shouldSortDescending ? 1 : -1;
    }
    // When they're equal, default to secondary sort.
    const firstSecondaryElement = a[remainingSortElements[0] as keyof CheckRow];
    const secondSecondaryElement = b[remainingSortElements[0] as keyof CheckRow];

    // When secondary elements are equal, roll with tertiary
    if (firstSecondaryElement === secondSecondaryElement) {
      const firstTertiaryElement = a[remainingSortElements[1] as keyof CheckRow];
      const secondTertiaryElement = b[remainingSortElements[1] as keyof CheckRow];
      return firstTertiaryElement > secondTertiaryElement ? 1 : -1;
    }
    return firstSecondaryElement > secondSecondaryElement ? 1 : -1;
  }

  // Note: Consider a secondary sort here as well
  const sortedChecks = sortParams.sort != null ? filteredChecks.sort(compare) : filteredChecks;

  // Now we sort

  const checksRows = sortedChecks.map((check) => (
    <LegendRow
      key={check.id}
      check={check}
      colorsMap={colorsMap}
      handleLegendClick={handleLegendClick}
      activeKeys={activeKeys}
      hoveredKey={hoveredKey}
      setHoveredKey={setHoveredKey}
    />
  ));

  return (
    <FullLegendContainer>
      <div>
        <StyledButton onClick={flipCollapse}>
          <StyledIconCaret expanded={!isCollapsed || searchTerm.length > 0} /> Metrics
          <CheckIcon>
            {' '}
            This table shows the overall pass rate for each Verification Check run for this
            Verification type. Click the check boxes to easily remove or add Checks to the graph
            above to view pass rates over time{' '}
          </CheckIcon>
        </StyledButton>
        <LegendSearch
          id='checks-table__search'
          handleInput={setSearchTerm}
          value={searchTerm}
          placeholder='Search'
          dataTest='checks-view__search-input'
          title=''
          flattenDisplay={isCollapsed || searchTerm.length > 0}
          size='md'
        />
      </div>
      <MetricsDivider />
      <Spacer height={1} />
      {(!isCollapsed || searchTerm.length !== 0) && !isEmpty && (
        <Container data-test={dataTest}>
          <TableLayout
            isEmpty={Object.keys(colorsMap).length === 0}
            showHeader={true}
            nullState={
              <TableNullState hasUsedFilters={true} dataTestBlock='checks-view'>
                <div>No matched results. Try adjusting your filters to view data.</div>
              </TableNullState>
            }
            headerTR={
              <>
                <CheckHeader canSort={false} id='checks-table__enabled-header'>
                  <Checkbox
                    autoFocus={false}
                    id='checks-table-all-enabled'
                    indeterminate={
                      activeKeys.length > 0 && activeKeys.length < Object.keys(colorsMap).length
                    }
                    checked={activeKeys.length > 0}
                    // When all are selected, remove all. Else, add all
                    onClick={() =>
                      activeKeys.length === Object.keys(colorsMap).length
                        ? // NOTE: This is a stupid hack since empty == full in the legend. Oof!
                          handleFillLegend([])
                        : handleFillLegend(Object.keys(colorsMap))
                    }
                  />
                </CheckHeader>

                <StyledHeader
                  onClick={() => handleColumnHeaderClick('name')}
                  canSort={canSort}
                  isSorted={sortParams.sort === 'name'}
                  isDescending={sortParams.shouldSortDescending}
                  id='checks-expanded-legend__name-header'
                >
                  Check
                </StyledHeader>
                <StyledHeader
                  onClick={() => handleColumnHeaderClick('requirement')}
                  canSort={canSort}
                  isSorted={sortParams.sort === 'requirement'}
                  isDescending={sortParams.shouldSortDescending}
                  id='checks-expanded-legend__requirement-header'
                >
                  Requirement{' '}
                  <HelpIcon>
                    {' '}
                    Whether or not the check in question is required for given template(s) selected.
                  </HelpIcon>
                </StyledHeader>
                <StyledHeader
                  onClick={() => handleColumnHeaderClick('pass_rate')}
                  canSort={canSort}
                  isSorted={sortParams.sort === 'pass_rate'}
                  isDescending={sortParams.shouldSortDescending}
                  id='checks-expanded-legend__status-header'
                >
                  Overall pass rate{' '}
                  <HelpIcon>
                    {' '}
                    The percentage of checks that have passed during the timeframe for processed
                    verifications.
                  </HelpIcon>
                </StyledHeader>
              </>
            }
          >
            {checksRows}
          </TableLayout>
        </Container>
      )}
    </FullLegendContainer>
  );
};

interface LegendRowProps {
  check: CheckRow;
  colorsMap: Partial<Record<CheckName, string>>;
  handleLegendClick: (dataKey: string) => void;
  activeKeys: string[];
  hoveredKey?: string;
  setHoveredKey: (dataKey: string | undefined) => void;
}

const LegendRow = ({
  check,
  colorsMap,
  handleLegendClick,
  activeKeys,
  hoveredKey,
  setHoveredKey,
}: LegendRowProps) => {
  return (
    <StyledRow
      data-test={`checks-expanded-legend-row__${check.id}`}
      id='checks-expanded-legend__row'
    >
      <StyledTD>
        <LegendText>
          <Checkbox
            autoFocus={false}
            id={check.id}
            checked={activeKeys.includes(check.id)}
            onClick={() => handleLegendClick(check.id)}
            onMouseEnter={() => setHoveredKey(check.id)}
            onMouseLeave={() => setHoveredKey(undefined)}
          />
        </LegendText>
      </StyledTD>
      <StyledTD>
        <LegendText
          onMouseEnter={() => setHoveredKey(check.id)}
          onMouseLeave={() => setHoveredKey(undefined)}
        >
          <ColoredCircle
            color={colorsMap[check.id as CheckName]}
            active={activeKeys.includes(check.id) || hoveredKey === check.id}
            onMouseEnter={() => setHoveredKey(check.id)}
            onMouseLeave={() => setHoveredKey(undefined)}
          />
          {check.name}
          <CheckIcon>{check.description}</CheckIcon>
        </LegendText>
      </StyledTD>
      <StyledTD>
        <LegendText>{check.requirement}</LegendText>
      </StyledTD>
      <StyledTD>
        <LegendText>{formatNumber(check.pass_rate)}%</LegendText>
      </StyledTD>
    </StyledRow>
  );
};

export default ChecksExpandedLegend;
